/**
 * VoiceClient — in-match voice chat over a WebRTC peer-to-peer mesh.
 *
 * One RTCPeerConnection per remote peer. Socket.IO (via NetworkManager) carries
 * signaling; this class owns mic capture, per-peer PCs, remote audio elements,
 * push-to-talk (local track enable), per-peer mute (audio element), and
 * voice-activity detection that drives the speaking indicators.
 *
 * Ported from Phone Dice's battle-tested `YoCall.js` MESH path (the live one).
 * Dropped from that port: the dead 1v1/ringing flow, cordova/StreetDice
 * globals, and the hardcoded Metered ICE list. The key fix over YoCall: ICE
 * servers come from the server's `voice:welcome` (short-lived Cloudflare TURN
 * creds), so nothing is hardcoded in the bundle.
 *
 * Peer identity is the game `playerId` (matches the server relay + the
 * `game:update` roster). Offer tie-break is deterministic: the LOWER playerId
 * offers, so exactly one side initiates per pair.
 */

import type { NetworkManager } from '../NetworkManager';
import type { VoiceIceServer } from '@sdl/shared';

/** STUN-only fallback if the server hasn't delivered ICE yet. */
const DEFAULT_ICE: VoiceIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];
/** Opus target bitrate — voice is fine at 24 kbps and keeps TURN egress low. */
const AUDIO_BITRATE = 24_000;

export interface VoiceCallbacks {
  /** A peer started/stopped speaking (drives the mic indicator on their card). */
  onSpeaking?(peerId: string, speaking: boolean): void;
  /** A peer's local mute state changed (drives the speaker/mute icon). */
  onPeerMuteChange?(peerId: string, muted: boolean): void;
}

function isIOS(): boolean {
  const ua = navigator.userAgent || '';
  // iPadOS 13+ reports as Macintosh but is touch-capable.
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && 'ontouchend' in document);
}

/** A TCP ICE candidate — filtered out on iOS, where they cause long stalls. */
function isTcpCandidate(c: RTCIceCandidateInit): boolean {
  const line = c?.candidate || '';
  return /\btcptype\s+/.test(line) || /\s+tcp\s+/.test(line);
}

/** Tighten Opus for low-latency mono voice and strip TCP candidate lines. */
function mungeOpusLowLatency(sdp: string): string {
  sdp = sdp.replace(/a=fmtp:(111|109|110) .*\r?\n/g, '');
  sdp = sdp.replace(
    /a=rtpmap:(111|109|110) opus\/48000\/2\r?\n/,
    (m) => m + `a=fmtp:111 minptime=10;maxplayoutdelay=40;maxaveragebitrate=24000;stereo=0;useinbandfec=1;usedtx=1\r\n`
  );
  if (!/a=ptime:20/.test(sdp)) sdp += 'a=ptime:20\r\n';
  if (!/a=maxptime:20/.test(sdp)) sdp += 'a=maxptime:20\r\n';
  sdp = sdp.replace(/^a=candidate:.* tcp .*$/gim, '');
  return sdp;
}

interface VadEntry {
  src: MediaStreamAudioSourceNode;
  analyser: AnalyserNode;
  data: Uint8Array<ArrayBuffer>;
  lastTs: number;
  speaking: boolean;
}

const VAD_CFG = { rmsOn: 0.008, rmsOff: 0.005, minOnMs: 90, minOffMs: 180, pollMs: 50 };

export class VoiceClient {
  private readonly nm: NetworkManager;
  private readonly cbs: VoiceCallbacks;
  private readonly ios = isIOS();

  private localId = '';
  private localStream: MediaStream | null = null;
  private ensuringMic: Promise<MediaStream | null> | null = null;
  private iceServers: VoiceIceServer[] = DEFAULT_ICE;
  /** Push-to-talk: mic starts muted, unmuted only while the button is held. */
  private muted = true;
  /** Incoming-voice playback level (0..1) from the Settings slider. */
  private playbackVolume = 1;
  private joined = false;

  private readonly pcs = new Map<string, RTCPeerConnection>();
  private readonly audioEls = new Map<string, HTMLAudioElement>();
  private readonly peerMute = new Map<string, boolean>();
  /** Per-peer op serialization (perfect-negotiation glare safety). */
  private readonly peerOps = new Map<string, Promise<void>>();
  private readonly roster = new Set<string>();

  // Voice-activity detection
  private audioCtx: AudioContext | null = null;
  private readonly vad = new Map<string, VadEntry>();
  private vadTimer: ReturnType<typeof setInterval> | null = null;

  constructor(nm: NetworkManager, cbs: VoiceCallbacks = {}) {
    this.nm = nm;
    this.cbs = cbs;
  }

  // == public API ===========================================================

  /** Join the match voice mesh: capture mic, subscribe, announce to server. */
  async join(): Promise<void> {
    if (this.joined) return;
    this.joined = true;
    this.localId = this.nm.getPlayerId() ?? '';
    await this.ensureMic();
    this.applyMuteToTrack();
    this.registerSignaling();
    this.nm.sendVoiceJoin();
  }

  /** Leave the mesh: close peers, stop mic, unsubscribe. Idempotent. */
  leave(): void {
    if (!this.joined) return;
    this.joined = false;
    try { this.nm.sendVoiceLeave(); } catch { /* socket gone */ }
    for (const peerId of [...this.pcs.keys()]) this.closePeer(peerId);
    this.unregisterSignaling();
    this.stopMic();
    this.roster.clear();
    this.stopVadLoop();
  }

  /** Push-to-talk: enable (false) / disable (true) the local mic broadcast. */
  setMuted(muted: boolean): void {
    this.muted = muted;
    this.applyMuteToTrack();
  }

  isMuted(): boolean {
    return this.muted;
  }

  /** Locally mute/unmute a specific peer (their audio element). */
  setPeerMuted(peerId: string, muted: boolean): void {
    this.peerMute.set(peerId, muted);
    const a = this.audioEls.get(peerId);
    if (a) { a.muted = muted; a.volume = muted ? 0 : this.playbackVolume; }
    this.cbs.onPeerMuteChange?.(peerId, muted);
  }

  /** Set incoming-voice playback level (0..1) for all peers (respects mute). */
  setPlaybackVolume(v: number): void {
    this.playbackVolume = Math.max(0, Math.min(1, v));
    for (const [peerId, a] of this.audioEls) {
      if (!this.isPeerMuted(peerId)) a.volume = this.playbackVolume;
    }
  }

  togglePeerMute(peerId: string): boolean {
    const next = !(this.peerMute.get(peerId) === true);
    this.setPeerMuted(peerId, next);
    return next;
  }

  isPeerMuted(peerId: string): boolean {
    return this.peerMute.get(peerId) === true;
  }

  /** Resume the AudioContext after a user gesture (iOS autoplay policy). */
  resumeAudio(): void {
    if (this.audioCtx && this.audioCtx.state !== 'running') {
      this.audioCtx.resume().catch(() => {});
    }
  }

  dispose(): void {
    this.leave();
    if (this.audioCtx) { this.audioCtx.close().catch(() => {}); this.audioCtx = null; }
  }

  // == signaling subscription ==============================================

  private registerSignaling(): void {
    this.nm.on('voice:welcome', this.onWelcome);
    this.nm.on('voice:peer-joined', this.onPeerJoined);
    this.nm.on('voice:peer-left', this.onPeerLeft);
    this.nm.on('voice:offer', this.onOffer);
    this.nm.on('voice:answer', this.onAnswer);
    this.nm.on('voice:ice', this.onIce);
    this.nm.on('voice:end', this.onEnd);
  }

  private unregisterSignaling(): void {
    this.nm.off('voice:welcome', this.onWelcome);
    this.nm.off('voice:peer-joined', this.onPeerJoined);
    this.nm.off('voice:peer-left', this.onPeerLeft);
    this.nm.off('voice:offer', this.onOffer);
    this.nm.off('voice:answer', this.onAnswer);
    this.nm.off('voice:ice', this.onIce);
    this.nm.off('voice:end', this.onEnd);
  }

  private onWelcome = (d: { iceServers: VoiceIceServer[]; peers: string[] }): void => {
    if (d.iceServers?.length) this.iceServers = d.iceServers; // adopt server TURN creds
    for (const pid of d.peers) this.connectToPeer(pid);
  };

  private onPeerJoined = (d: { from: string }): void => {
    this.connectToPeer(d.from);
  };

  private onPeerLeft = (d: { from: string }): void => {
    this.closePeer(d.from);
  };

  private onOffer = (d: { from: string; sdp: RTCSessionDescriptionInit }): void => {
    void this.handleOffer(d.from, d.sdp);
  };

  private onAnswer = (d: { from: string; sdp: RTCSessionDescriptionInit }): void => {
    void this.handleAnswer(d.from, d.sdp);
  };

  private onIce = (d: { from: string; candidate: RTCIceCandidateInit }): void => {
    void this.handleIce(d.from, d.candidate);
  };

  private onEnd = (d: { from: string }): void => {
    this.closePeer(d.from);
  };

  /** Deterministic connect: lower playerId offers, the other waits for it. */
  private connectToPeer(peerId: string): void {
    if (!peerId || peerId === this.localId) return;
    this.roster.add(peerId);
    if (this.localId < peerId) void this.offerTo(peerId);
    else void this.getOrCreatePC(peerId); // warm the PC; their offer will arrive
  }

  // == peer connections =====================================================

  private async getOrCreatePC(peerId: string): Promise<RTCPeerConnection> {
    const existing = this.pcs.get(peerId);
    if (existing) return existing;

    const pc = new RTCPeerConnection({ iceServers: this.iceServers as RTCIceServer[], bundlePolicy: 'max-bundle' });
    this.pcs.set(peerId, pc);

    pc.ontrack = (e) => {
      const stream = e.streams && e.streams[0] ? e.streams[0] : new MediaStream([e.track]);
      const a = this.peerAudio(peerId);
      if (a.srcObject !== stream) {
        a.srcObject = stream;
        a.play().catch(() => {});
        this.attachVad(peerId, stream);
      }
    };

    pc.onicecandidate = (e) => {
      if (e.candidate) this.nm.sendVoiceIce(peerId, e.candidate.toJSON());
    };

    const stream = await this.ensureMic();
    if (stream) {
      const hasAudio = pc.getSenders().some((s) => s.track && s.track.kind === 'audio');
      if (!hasAudio) {
        const track = stream.getAudioTracks()[0];
        if (track) { pc.addTrack(track, stream); this.capBitrate(pc); }
      }
    }
    return pc;
  }

  private async offerTo(peerId: string): Promise<void> {
    return this.withPeerOp(peerId, async () => {
      const pc = await this.getOrCreatePC(peerId);
      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      if (offer.sdp) offer.sdp = mungeOpusLowLatency(offer.sdp);
      await pc.setLocalDescription(offer);
      this.nm.sendVoiceOffer(peerId, offer);
    });
  }

  private async handleOffer(peerId: string, sdp: RTCSessionDescriptionInit): Promise<void> {
    return this.withPeerOp(peerId, async () => {
      const pc = await this.getOrCreatePC(peerId);
      const collision = pc.signalingState !== 'stable';
      const polite = this.localId > peerId; // higher id yields on glare
      if (collision && !polite) return;
      if (collision && polite) {
        try { await pc.setLocalDescription({ type: 'rollback' }); } catch { /* ignore */ }
      }
      await pc.setRemoteDescription(sdp);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      this.nm.sendVoiceAnswer(peerId, answer);
    });
  }

  private async handleAnswer(peerId: string, sdp: RTCSessionDescriptionInit): Promise<void> {
    return this.withPeerOp(peerId, async () => {
      const pc = this.pcs.get(peerId);
      if (!pc || pc.signalingState !== 'have-local-offer') return;
      try {
        await pc.setRemoteDescription(sdp);
      } catch (e) {
        // Late/duplicate answer after we already went stable — benign.
        const name = (e as DOMException)?.name || '';
        if (name === 'InvalidStateError' || name === 'OperationError') return;
        throw e;
      }
    });
  }

  private async handleIce(peerId: string, cand: RTCIceCandidateInit): Promise<void> {
    if (!cand) return;
    if (this.ios && isTcpCandidate(cand)) return;
    const pc = this.pcs.get(peerId);
    if (!pc) return;
    try { await pc.addIceCandidate(cand); } catch { /* pre-remote-desc candidate, dropped */ }
  }

  private closePeer(peerId: string): void {
    this.detachVad(peerId);
    const pc = this.pcs.get(peerId);
    if (pc) {
      try { pc.ontrack = null; pc.onicecandidate = null; pc.close(); } catch { /* already closed */ }
      this.pcs.delete(peerId);
    }
    const a = this.audioEls.get(peerId);
    if (a) {
      try { a.pause(); a.srcObject = null; } catch { /* noop */ }
      a.remove();
      this.audioEls.delete(peerId);
    }
    this.peerMute.delete(peerId);
    this.peerOps.delete(peerId);
    this.roster.delete(peerId);
  }

  /** Serialize per-peer SDP ops so glare/rollback can't interleave. */
  private withPeerOp(peerId: string, fn: () => Promise<void>): Promise<void> {
    const prev = this.peerOps.get(peerId) ?? Promise.resolve();
    const next = prev.then(fn, fn);
    this.peerOps.set(peerId, next.catch(() => {}));
    return next;
  }

  private capBitrate(pc: RTCPeerConnection): void {
    const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'audio');
    if (!sender) return;
    const params = sender.getParameters();
    if (!params.encodings || params.encodings.length === 0) params.encodings = [{}];
    params.encodings[0].maxBitrate = AUDIO_BITRATE;
    sender.setParameters(params).catch(() => {});
  }

  // == mic + audio elements =================================================

  private async ensureMic(): Promise<MediaStream | null> {
    if (this.localStream && this.localStream.active) return this.localStream;
    if (this.ensuringMic) return this.ensuringMic;

    this.ensuringMic = navigator.mediaDevices
      .getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: false, channelCount: 1 },
        video: false,
      })
      .then((stream) => { this.localStream = stream; this.applyMuteToTrack(); return stream; })
      .catch(() => null);

    const s = await this.ensuringMic;
    this.ensuringMic = null;
    return s;
  }

  private applyMuteToTrack(): void {
    const track = this.localStream?.getAudioTracks()[0];
    if (track) track.enabled = !this.muted;
  }

  private stopMic(): void {
    if (this.localStream) {
      this.localStream.getTracks().forEach((t) => { try { t.stop(); } catch { /* noop */ } });
      this.localStream = null;
    }
  }

  private peerAudio(peerId: string): HTMLAudioElement {
    let a = this.audioEls.get(peerId);
    if (!a) {
      a = document.createElement('audio');
      a.autoplay = true;
      a.muted = this.peerMute.get(peerId) === true;
      a.volume = a.muted ? 0 : this.playbackVolume;
      a.setAttribute('playsinline', '');
      a.style.position = 'absolute';
      a.style.left = '-9999px';
      a.style.width = '1px';
      a.style.height = '1px';
      document.body.appendChild(a);
      this.audioEls.set(peerId, a);
    }
    return a;
  }

  // == voice-activity detection ============================================

  private ensureAudioCtx(): AudioContext {
    if (!this.audioCtx) {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioCtx = new Ctor();
    }
    return this.audioCtx;
  }

  private attachVad(peerId: string, stream: MediaStream): void {
    try {
      const ctx = this.ensureAudioCtx();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      this.vad.set(peerId, { src, analyser, data: new Uint8Array(analyser.fftSize), lastTs: performance.now(), speaking: false });
      if (!this.vadTimer) this.startVadLoop();
    } catch { /* AudioContext unavailable — speaking indicators just won't light */ }
  }

  private detachVad(peerId: string): void {
    const v = this.vad.get(peerId);
    if (!v) return;
    try { v.src.disconnect(); } catch { /* noop */ }
    this.vad.delete(peerId);
    if (v.speaking) this.cbs.onSpeaking?.(peerId, false);
    if (this.vad.size === 0) this.stopVadLoop();
  }

  private startVadLoop(): void {
    if (this.vadTimer) return;
    this.vadTimer = setInterval(() => {
      const now = performance.now();
      for (const [peerId, v] of this.vad) {
        if (this.isPeerMuted(peerId)) {
          if (v.speaking) { v.speaking = false; v.lastTs = now; this.cbs.onSpeaking?.(peerId, false); }
          continue;
        }
        v.analyser.getByteTimeDomainData(v.data);
        let sum = 0;
        for (let i = 0; i < v.data.length; i++) { const x = (v.data[i] - 128) / 128; sum += x * x; }
        const rms = Math.sqrt(sum / v.data.length);
        const elapsed = now - v.lastTs;
        if (!v.speaking && rms >= VAD_CFG.rmsOn && elapsed >= VAD_CFG.minOffMs) {
          v.speaking = true; v.lastTs = now; this.cbs.onSpeaking?.(peerId, true);
        } else if (v.speaking && rms <= VAD_CFG.rmsOff && elapsed >= VAD_CFG.minOnMs) {
          v.speaking = false; v.lastTs = now; this.cbs.onSpeaking?.(peerId, false);
        }
      }
    }, VAD_CFG.pollMs);
  }

  private stopVadLoop(): void {
    if (this.vadTimer) { clearInterval(this.vadTimer); this.vadTimer = null; }
  }
}
